import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Wallet, MessageCircle, ExternalLink, Loader2, Filter } from "lucide-react";
import { toast } from "sonner";
import { listMyPendingCharges } from "@/lib/api/parent-students.functions";
import { paymentMethodLabel, paymentMethodChannel } from "@/lib/payment-methods";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/responsavel/cobranca")({
  head: () => ({
    meta: [
      { title: "Cobrança | FutBux" },
      { name: "description", content: "Todas as cobranças pendentes dos seus alunos em um só lugar." },
      { property: "og:title", content: "Cobrança | FutBux" },
      { property: "og:description", content: "Acompanhe e pague os débitos pendentes dos seus alunos." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const fmtBRL = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d?: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—");

const KIND_LABEL: Record<string, string> = {
  plan: "Plano",
  product: "Produto",
  uniform: "Uniforme",
};

function Page() {
  const navigate = useNavigate();
  const fn = useServerFn(listMyPendingCharges);
  const [charges, setCharges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [studentFilter, setStudentFilter] = useState<string>("all");

  const load = async () => {
    try {
      const res = await fn();
      setCharges(res.charges ?? []);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar cobranças.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const students = useMemo(() => {
    const map = new Map<string, string>();
    charges.forEach((c) => c.student?.id && map.set(c.student.id, c.student.name));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [charges]);

  const filtered = charges.filter((c) => {
    if (kindFilter !== "all" && c.kind !== kindFilter) return false;
    if (studentFilter !== "all" && c.student?.id !== studentFilter) return false;
    return true;
  });

  const total = filtered.reduce((acc, c) => acc + Number(c.amount || 0), 0);

  const onWhatsAppTc = (c: any) => {
    const phone = (c.student?.training_centers?.whatsapp || "").replace(/\D/g, "");
    if (!phone) { toast.error("Centro sem WhatsApp cadastrado."); return; }
    const msg = encodeURIComponent(
      `Olá! Gostaria de acertar o pagamento pendente:\n\n` +
      `• Aluno: ${c.student?.name}\n` +
      `• ${KIND_LABEL[c.kind] ?? c.kind}: ${c.description}\n` +
      `• Valor: ${fmtBRL(Number(c.amount || 0))}`
    );
    window.open(`https://wa.me/55${phone}?text=${msg}`, "_blank");
  };

  if (loading) return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet className="w-6 h-6 text-primary" /> Cobrança</h1>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Total pendente</div>
          <div className="text-xl font-bold text-primary">{fmtBRL(total)}</div>
        </div>
      </div>

      {/* filtros */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="plan">Planos</SelectItem>
              <SelectItem value="product">Produtos</SelectItem>
              <SelectItem value="uniform">Uniformes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={studentFilter} onValueChange={setStudentFilter}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os alunos</SelectItem>
              {students.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            🎉 Nenhuma cobrança pendente.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const channel = paymentMethodChannel(c.payment_method);
            return (
              <Card key={c.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="truncate">{c.student?.name || "Aluno"}</span>
                    <Badge variant="outline">{KIND_LABEL[c.kind] ?? c.kind}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm">{c.description}</div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {c.due_date ? `Vencimento: ${fmtDate(c.due_date)}` : `Criado: ${fmtDate(String(c.created_at).slice(0, 10))}`}
                    </span>
                    <span className="font-bold text-primary text-lg">{fmtBRL(Number(c.amount || 0))}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Forma: {paymentMethodLabel(c.payment_method)}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {c.pay_url && channel === "comprovante" && (
                      <Button size="sm" onClick={() => navigate({ to: c.pay_url as any })}>
                        <ExternalLink className="w-4 h-4 mr-1" /> Ver Pix
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => onWhatsAppTc(c)}>
                      <MessageCircle className="w-4 h-4 mr-1" /> Falar com o CT
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
